// Responsible for loading and storing the type delarations that should be
// included in the monaco editor. Such as the jQuery and jQuery UI types
// for autocompletion.

import { notEmpty } from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { deferredToPromise, getLocaleCode, getServerStartTime } from "../Util.ts";

// en is the default (fallback)
const SupportedLocales = ["de"];

/**
 * Serves as a cache for type definitions. Loaded by the monaco editor.
 */
const TypeDefinitions: Record<string, string> = {};

/**
 * @param Name of the type declaration file to retrieve
 * @returns The content of the (cached) type declaration file, or `undefined`
 * if it does not exist.
 */
export function getTypeDefinition(name: string): string | undefined {
  return TypeDefinitions[name];
}

/**
 * Caches the given content of a type declaration file.
 * @param name Name of the type definition to add.
 * @param content Content of the type declaration file.
 */
export function setTypeDefintion(name: string, content: string): void {
  TypeDefinitions[name] = content;
}

/**
 * List of default type definitions files for TypeScript.
 * @returns List of default types.
 */
export function getDefaultTypes(): string[] {
  const localeCode = getLocaleCode();
  const localeSuffix = SupportedLocales.indexOf(localeCode) >= 0 ? `-${localeCode}` : "";
  const libs = [];
  libs.push("sizzle", "jquery", "autoNumeric", `formcycle${localeSuffix}`);
  return libs;
}

/**
 * Creates a Promise that loads the type declaration files via HTTP and resolves once they have been added to the monaco
 * editor.
 * @param types List of types.
 * @returns The promise that loads the unloaded types.
 */
export function createTypeDefThenable(
  types: string[],
): Promise<import("monaco-editor").editor.IEditorConstructionOptions | undefined> | undefined {
  const toBeLoaded = [] as string[];

  // Check which type definitions need to be loaded.
  for (const type of types) {
    const data = getTypeDefinition(type);
    if (notEmpty(data)) {
      monaco.languages.typescript.javascriptDefaults.addExtraLib(data, `inmemory:root/${type}.d.ts`);
    } else {
      toBeLoaded.push(type);
    }
  }

  if (toBeLoaded.length === 0) {
    // Nothing to be loaded via AJAX
    return undefined;
  }

  // Create promises for the .d.ts files
  const promises = toBeLoaded.map((type) => {
    return $.ajax({
      accepts: { text: "text/plain; charset=utf-8" },
      cache: true,
      dataType: "text",
      method: "GET",
      url: PrimeFaces.resources.getFacesResource(`types/${type}.d.ts`, "formcycle", getServerStartTime()),
    });
  });

  // Load the .d.ts files into the editor once loaded
  const promise = $.when
    .call($, ...promises)
    .then((...args) => {
      // jquery sucks: the arguments are of different type depending on the number of arguments passed to 'when'
      const responses = (Array.isArray(args[0]) ? args : [args]) as unknown as string[];
      $.each(responses, (index, response) => {
        const typeDefinition = response[0];
        const file = toBeLoaded[index];
        if (file !== undefined && typeDefinition !== undefined) {
          setTypeDefintion(file, typeDefinition);
          monaco.languages.typescript.javascriptDefaults.addExtraLib(typeDefinition, `inmemory:root/${file}.d.ts`);
        }
      });
      return undefined;
    })
    .catch((e) => {
      console.error("Could not load type definition files: ", toBeLoaded, e);
      return undefined;
    });

  return deferredToPromise(promise);
}
